import React, { useCallback, useEffect, useRef, useState } from "react";
import { Mic, X, Send, Square } from "lucide-react";
import { useI18n } from "@/contexts/LanguageContext";

/**
 * VoiceRecorder — inline recorder.
 *
 * Props:
 *   onCancel(): dismiss without sending
 *   onSend(blob, durationMs, mime): send the recorded audio
 *   busy: disable send button while parent uploads
 *
 * Behaviour:
 *   * requests mic on mount (user-gesture must already have happened)
 *   * shows a growing timer while recording
 *   * "Stop" ends recording -> preview state
 *   * "Send" uploads
 *   * "Cancel" discards
 */
export default function VoiceRecorder({ onCancel, onSend, busy }) {
  const { t } = useI18n();
  const [error, setError] = useState("");
  const [recording, setRecording] = useState(false);
  const [durationMs, setDurationMs] = useState(0);
  const [blob, setBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const startTimeRef = useRef(0);
  const tickRef = useRef(null);
  const mimeRef = useRef("audio/webm");

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    }
  };

  const startRecording = useCallback(async () => {
    setError("");
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      setError(t("app.messaging.micUnavailable"));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      let mimeType = "audio/webm;codecs=opus";
      if (!window.MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = "audio/webm";
        if (!window.MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = "audio/mp4";
          if (!window.MediaRecorder.isTypeSupported(mimeType)) mimeType = "";
        }
      }
      mimeRef.current = mimeType || "audio/webm";
      const mr = new window.MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        const finalMime = mimeRef.current.split(";")[0] || "audio/webm";
        const b = new Blob(chunksRef.current, { type: finalMime });
        setBlob(b);
        setAudioUrl(URL.createObjectURL(b));
        stopStream();
      };
      mediaRecorderRef.current = mr;
      startTimeRef.current = Date.now();
      mr.start(250);
      setRecording(true);
      tickRef.current = setInterval(() => {
        setDurationMs(Date.now() - startTimeRef.current);
      }, 200);
    } catch (e) {
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        setError(t("app.messaging.micDenied"));
      } else {
        setError(t("app.messaging.micUnavailable"));
      }
    }
  }, [t]);

  const stopRecording = () => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") mr.stop();
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    setRecording(false);
  };

  const cancel = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try { mediaRecorderRef.current.stop(); } catch {}
    }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    stopStream();
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    onCancel();
  };

  const send = () => {
    if (!blob) return;
    onSend(blob, durationMs, mimeRef.current);
  };

  // Kick off recording immediately on mount
  useEffect(() => {
    startRecording();
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      stopStream();
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatTime = (ms) => {
    const s = Math.floor(ms / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  };

  if (error) {
    return (
      <div
        className="w-full flex items-center gap-3 p-3 rounded-md border"
        style={{ borderColor: "hsl(var(--destructive))" }}
        data-testid="recorder-error"
      >
        <p className="flex-1 text-sm" style={{ color: "hsl(var(--destructive))" }}>{error}</p>
        <button onClick={cancel} className="nvl-btn nvl-btn-outline" data-testid="recorder-close-error">
          {t("app.messaging.cancel")}
        </button>
      </div>
    );
  }

  return (
    <div
      className="w-full flex items-center gap-3 p-3 rounded-md border"
      style={{ borderColor: "hsl(var(--nvl-border))", backgroundColor: "hsl(var(--muted))" }}
      data-testid="voice-recorder"
    >
      {recording ? (
        <>
          <span
            className="w-3 h-3 rounded-full animate-pulse"
            style={{ backgroundColor: "hsl(var(--nvl-accent))" }}
          />
          <span className="text-sm" data-testid="recorder-duration">
            {t("app.messaging.recording")} · {formatTime(durationMs)}
          </span>
          <div className="flex-1" />
          <button
            type="button"
            onClick={cancel}
            className="nvl-btn nvl-btn-outline"
            data-testid="recorder-cancel"
            aria-label={t("app.messaging.cancel")}
          >
            <X size={14} />
          </button>
          <button
            type="button"
            onClick={stopRecording}
            className="nvl-btn nvl-btn-primary"
            data-testid="recorder-stop"
            aria-label={t("app.messaging.stop")}
          >
            <Square size={14} />
          </button>
        </>
      ) : blob ? (
        <>
          <audio src={audioUrl} controls className="flex-1 h-9" data-testid="recorder-preview" />
          <span className="text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }} data-testid="recorder-preview-duration">
            {formatTime(durationMs)}
          </span>
          <button
            type="button"
            onClick={cancel}
            className="nvl-btn nvl-btn-outline"
            data-testid="recorder-discard"
            aria-label={t("app.messaging.cancel")}
          >
            <X size={14} />
          </button>
          <button
            type="button"
            onClick={send}
            disabled={busy}
            className="nvl-btn nvl-btn-primary"
            data-testid="recorder-send"
            aria-label={t("app.messaging.send")}
          >
            <Send size={14} />
          </button>
        </>
      ) : (
        <span className="text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
          <Mic size={14} className="inline mr-2" />
          {t("app.messaging.startRecording")}
        </span>
      )}
    </div>
  );
}
